import * as AvatarPrimitive from '@radix-ui/react-avatar'
import * as React from 'react'

import { cn } from '@/shared/lib/utils'

type AvatarSeed = string | number

type BrandAvatarPalette = {
  base: string
  middle: string
  accent: string
}

/**
 * Curated variations of IDC's navy, blue, light blue and orange. Variation
 * comes from placement and pairing, never from leaving the brand spectrum.
 */
const BRAND_AVATAR_PALETTES: readonly BrandAvatarPalette[] = [
  { base: '#0F2554', middle: '#0053C8', accent: '#B1D2FF' },
  { base: '#0F2554', middle: '#234A86', accent: '#FFA53A' },
  { base: '#102A5B', middle: '#3566A8', accent: '#8DB9F5' },
  { base: '#0B1D42', middle: '#244C86', accent: '#D87E22' },
  { base: '#183A70', middle: '#0053C8', accent: '#E7902C' },
  { base: '#132F68', middle: '#315F9C', accent: '#B1D2FF' }
]

function hashAvatarSeed(seed: AvatarSeed): number {
  const value = String(seed)
  let hash = 2166136261

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return hash >>> 0
}

function brandedAvatarBackground(seed: AvatarSeed): string {
  const hash = hashAvatarSeed(seed)
  const palette = BRAND_AVATAR_PALETTES[hash % BRAND_AVATAR_PALETTES.length]
  const accentX = 12 + ((hash >>> 8) % 77)
  const accentY = 12 + ((hash >>> 16) % 77)
  const middleX = 100 - accentX
  const middleY = 100 - accentY
  const angle = 30 + (hash % 121)

  return [
    `radial-gradient(circle at ${accentX}% ${accentY}%, ${palette.accent} 0%, transparent 48%)`,
    `radial-gradient(circle at ${middleX}% ${middleY}%, ${palette.middle} 0%, transparent 56%)`,
    `linear-gradient(${angle}deg, ${palette.base} 0%, ${palette.middle} 100%)`
  ].join(', ')
}

function firstAvatarInitial(children: React.ReactNode): React.ReactNode {
  if (typeof children !== 'string' && typeof children !== 'number') return children ?? '?'

  return Array.from(String(children).trim())[0]?.toLocaleUpperCase('es-GT') ?? '?'
}

type AvatarProps = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
  /** Stable identity used to generate the fallback when the image is missing. */
  seed?: AvatarSeed | null
}

const AvatarSeedContext = React.createContext<AvatarSeed | null | undefined>(undefined)

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, seed, ...props }, ref) => (
    <AvatarSeedContext.Provider value={seed}>
      <AvatarPrimitive.Root
        ref={ref}
        data-slot="avatar"
        className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
        {...props}
      />
    </AvatarSeedContext.Provider>
  )
)
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    data-slot="avatar-image"
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ children, className, ...props }, ref) => {
  const seed = React.useContext(AvatarSeedContext)
  const hasSeed = seed !== undefined && seed !== null

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      data-slot="avatar-fallback"
      className={cn(
        'relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#0F2554] text-white',
        className
      )}
      {...props}
    >
      {hasSeed ? (
        <>
          <span
            aria-hidden="true"
            className="absolute inset-0"
            style={{ backgroundImage: brandedAvatarBackground(seed) }}
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(15,37,84,0.78)_0%,rgba(15,37,84,0.42)_38%,transparent_72%)]"
          />
          <span className="relative z-10 font-semibold uppercase leading-none drop-shadow-sm">
            {firstAvatarInitial(children)}
          </span>
        </>
      ) : (
        children
      )}
    </AvatarPrimitive.Fallback>
  )
})
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarFallback, AvatarImage }
